import "./ImageUploader.scss";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-bootstrap";
import {
  Page,
  OverlayTrigger,
  Dropdown,
  DropdownToggleType,
  Button,
  ButtonType,
  ButtonFrameworkVariant,
} from "../Components";
import * as R from "ramda";
import { IoMdArrowDropdown } from "react-icons/io";
import * as uuid from "uuid";
import { CompanySelector } from "../NavBar/CompanySelector";
import { GetS3SignedUrl2, S3Delete, S3PutPng } from "../utils/fetch-utils";
import { useSetError } from "../redux/modals";
import AlertDialog, { AlertDialogVariants } from "../Components/AlertDialog/AlertDialog";
import InfoSidebar, { InfoProps } from "../Components/InfoSidebar/InfoSidebar";
import { Network, useNetworkMap } from "../redux/networks";
import { Jimp } from "jimp";
import ImageUploadPreview from "./ImageUploadPreview";
import { CreativeMapItem, useCreativeMap } from "../redux/creative";
import { MdOutlineDeleteOutline, MdSave } from "react-icons/md";
import Select from "react-select";

type Uploader = Record<string, { path: string; types: string[]; filenames?: string[] }>;

// maps type of uploader to the S3 path and allowed file types
const UPLOADERS: Uploader = {
  "Client Logos": {
    path: "companies/",
    types: ["image/png"],
    filenames: ["logo_dark.png", "logo.png", "square_logo_dark.png", "square_logo.png"],
  },
  "Client Intro Slide": {
    path: "companies/",
    types: ["image/png"],
    filenames: ["powerpoint_intro.png"],
  },
  "Client Creative Thumbnail": {
    path: "creativeAssets/thumbnails/",
    types: ["image/png"],
  },
  "Network Logos": {
    path: "networks/",
    types: ["image/png"],
  },
};

// for dropdowns
const MAIN_DROPDOWN_OPTIONS = [
  { label: "Client Logos", value: "Client Logos" },
  { label: "Client Creative Thumbnail", value: "Client Creative Thumbnail" },
  { label: "Client Intro Slide", value: "Client Intro Slide" },
  { label: "Network Logos", value: "Network Logos" },
];

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2mb in bytes

const IMAGE_DIMENSIONS: Record<string, { width: number; height: number }> = {
  "logo.png": { width: 200, height: 100 },
  "logo_dark.png": { width: 200, height: 100 },
  "square_logo.png": { width: 100, height: 100 },
  "square_logo_dark.png": { width: 100, height: 100 },
  "powerpoint_intro.png": { width: 1280, height: 720 },
};

const bucketName = "bpm-cdn";

const FILE_RESTRICTIONS_INFO: InfoProps[] = [
  {
    title: "Company Logos",
    content: `We use **four logo variants…** two shapes (**square and horizontal**), and a version of each to use on **light and dark backgrounds**. Please follow the size and file naming guidelines carefully.

Important: These logos will be scaled down to slightly smaller than a phone app icon, so use a version that stays clear and recognizable at small sizes.

**Square Logos**
- **Dimensions**: 100px x 100px
- **For light backgrounds file name**: \`square_logo.png\`
- **For dark backgrounds file name**: \`square_logo_dark.png\`

**Horizontal Logos**
- **Dimensions**: 200px x 100px
  - **Note**: Even if your logo isn't exactly this shape, the uploaded image file must match these dimensions. These logos are used in prominent spots like the navigation bar, so clarity on both background types is important.
- **For light backgrounds file name**: \`logo.png\`
- **For dark backgrounds file name**: \`logo_dark.png\``,
  },
  {
    title: "Network Logos",
    content: `**Network logos have a standard size and specific naming**:
- **Dimensions**: 100px x 100px
- **File Name**: Save the image using the **exact short code for the network, in ALL CAPS, followed by** \`.png\`.
  - _Example_: If the network's short code is "NBC", the file name would be \`NBC.png\`.
- **Important**: These logos will be significantly shrunk down to about favicon size (the tiny icon you see in a browser tab), so use a version that is recognizable at small sizes.`,
  },
  {
    title: "Client Intro Slide",
    content: `The powerpoint cover slide must be 1280px by 720px.

Save the image as powerpoint_intro.png.`,
  },
];

const getS3ImgUrl = async (s3Key: string): Promise<string> => {
  try {
    return await GetS3SignedUrl2(bucketName, s3Key);
  } catch (err) {
    console.error("Error getting S3 signed URL:", err);
    return "";
  }
};

type UploaderState = {
  title: string;
  localFile: File | null;
  previewUrl: string | null;
  s3Key: string | null;
  isLoading: boolean;
};

const ImageUploader = (): JSX.Element => {
  const setError = useSetError();

  const [selectedUploader, setSelectedUploader] = useState<string>(MAIN_DROPDOWN_OPTIONS[0].label);
  const [selectedNetwork, setSelectedNetwork] = useState<string>("Select Network");
  const [selectedISCIs, setSelectedISCIs] = useState<string[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [newCompanySelector, setNewCompanySelector] = useState<boolean>();
  const [companySearchQuery, setCompanySearchQuery] = useState<string>("");

  const completeInfo =
    (selectedUploader === "Client Logos" && selectedCompany) ||
    (selectedUploader === "Client Intro Slide" && selectedCompany) ||
    (selectedUploader === "Client Creative Thumbnail" && selectedCompany && selectedISCIs[0]) ||
    (selectedUploader === "Network Logos" && selectedNetwork !== "Select Network");

  const getClearUploaderState = useCallback((): Record<string, UploaderState> => {
    if (!completeInfo) {
      return {};
    }
    if (selectedUploader === "Client Logos" && UPLOADERS["Client Logos"].filenames) {
      return UPLOADERS["Client Logos"].filenames.reduce((acc, filename) => {
        acc[filename] = {
          title:
            filename === "logo_dark.png"
              ? "Horizontal Logo Dark Background"
              : filename === "square_logo_dark.png"
              ? "Square Logo Dark Background"
              : filename === "logo.png"
              ? "Horizontal Logo Light Background"
              : filename === "square_logo.png"
              ? "Square Logo Light Background"
              : "",
          localFile: null,
          previewUrl: null,
          s3Key: `${UPLOADERS["Client Logos"].path + selectedCompany}/${filename}`,
          isLoading: false,
        };
        return acc;
      }, {} as Record<string, UploaderState>);
    } else if (selectedUploader === "Client Intro Slide") {
      return {
        "powerpoint_intro.png": {
          title: "Intro Slide",
          localFile: null,
          previewUrl: null,
          s3Key: `${UPLOADERS["Client Intro Slide"].path + selectedCompany}/powerpoint_intro.png`,
          isLoading: false,
        },
      };
    } else if (selectedUploader === "Client Creative Thumbnail") {
      return {
        [`${selectedISCIs[0]}.png`]: {
          title: "Thumbnail",
          localFile: null,
          previewUrl: null,
          s3Key: `${UPLOADERS["Client Creative Thumbnail"].path + selectedCompany}/${
            selectedISCIs[0]
          }.png`,
          isLoading: false,
        },
      };
    } else if (selectedUploader === "Network Logos") {
      return {
        [`${selectedNetwork}.png`]: {
          title: "Network Logo",
          localFile: null,
          previewUrl: null,
          s3Key: `${UPLOADERS["Network Logos"].path + selectedNetwork}.png`,
          isLoading: false,
        },
      };
    }
    return {};
  }, [selectedUploader, selectedCompany, selectedNetwork, selectedISCIs, completeInfo]);

  const [uploaders, setUploaders] = useState<Record<string, UploaderState>>(() =>
    getClearUploaderState()
  );

  useEffect(() => {
    setUploaders(getClearUploaderState());
  }, [selectedUploader, selectedCompany, selectedNetwork, selectedISCIs, getClearUploaderState]);

  const [s3Urls, setS3Urls] = useState<Record<string, string | null>>({});

  useEffect(() => {
    let isMounted = true;
    const fetchUrls = async () => {
      const entries = await Promise.all(
        Object.entries(uploaders).map(async ([key, uploader]) => {
          if (uploader.s3Key) {
            const url = await getS3ImgUrl(uploader.s3Key);
            return [key, url];
          }
          return [key, null];
        })
      );
      if (isMounted) {
        setS3Urls(Object.fromEntries(entries));
      }
    };
    fetchUrls();
    return () => {
      isMounted = false;
    };
  }, [uploaders]);

  const [dialog, setDialog] = useState<{
    show: boolean;
    title: string;
    body: string;
    variant?: AlertDialogVariants;
  } | null>(null);

  const [isUploading, setIsUploading] = useState<boolean>(false);

  const networkMap = useNetworkMap();
  const networks = useMemo(() => {
    if (networkMap) {
      return R.pipe(
        R.toPairs,
        R.filter(([_, network]) => (network as Network).buyable && !(network as Network).parent),
        R.sortBy(([shortCode, _]) => shortCode),
        R.fromPairs
      )(networkMap);
    }
  }, [networkMap]);

  const NETWORK_DROPDOWN_OPTIONS = useMemo(() => {
    const networkShortCodes = networks ? Object.keys(networks) : [];
    return networkShortCodes
      ? [{ label: "Select Network", value: "Select Network" }].concat(
          networkShortCodes.map(code => ({ label: code, value: code }))
        )
      : [];
  }, [networks]);

  const { creativeMap } = useCreativeMap({
    company: selectedCompany,
    mediaTypes: ["streaming", "audio", "linear", "display"],
  });

  const modelEntryMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    if (creativeMap) {
      Object.values(creativeMap).forEach((item: CreativeMapItem) => {
        const { modelEntry, isci } = item;
        if (!modelEntry || !isci) {
          return;
        }
        const key = modelEntry.toString();
        if (!map[key]) {
          map[key] = [];
        }
        map[key].push(isci);
      });
    }
    return map;
  }, [creativeMap]);

  const ISCI_DROPDOWN_OPTIONS = useMemo(() => {
    if (!creativeMap) {
      return [];
    }

    return Object.keys(creativeMap).map(isci => {
      return { label: isci, value: isci };
    });
  }, [creativeMap]);

  const handleISCIChange = (selectedVals: { label: string; value: string }[] | null) => {
    if (!selectedVals || selectedVals.length === 0) {
      setSelectedISCIs([]);
      return;
    }
    let newSelectedISCIs: string[];
    // if nothing is selected yet, and the selected ISCI has a modelEntry, select all ISCIs with that modelEntry
    // we only want to do it if nothing is selected yet because otherwise it would re-select all ISCIs
    // with the same modelEntry every time you click one and it would make it harder to un-select them
    if (
      selectedISCIs.length === 0 &&
      creativeMap &&
      creativeMap[selectedVals[0].value] &&
      creativeMap[selectedVals[0].value].modelEntry !== undefined &&
      modelEntryMap[String(creativeMap[selectedVals[0].value].modelEntry)]
    ) {
      newSelectedISCIs = [...modelEntryMap[String(creativeMap[selectedVals[0].value].modelEntry)]];
    } else {
      newSelectedISCIs = selectedVals.map(v => v.value);
    }
    setSelectedISCIs(newSelectedISCIs);
  };

  const handleMediaUpload = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target; // to store ref during async callback
    const selectedFile = input.files?.[0] ?? null;
    if (!selectedFile) {
      return;
    }

    // FILE SIZE CHECK
    if (selectedFile.size > MAX_FILE_SIZE) {
      setDialog({
        show: true,
        title: "Invalid File Size",
        body: `File size must be less than 2 MB. Your file is ${(
          selectedFile.size /
          1024 /
          1024
        ).toFixed(2)} MB.`,
        variant: AlertDialogVariants.ERROR,
      });
      return;
    }

    // FILE TYPE CHECK
    const { types } = UPLOADERS[selectedUploader];
    if (!types.includes(selectedFile.type)) {
      setDialog({
        show: true,
        title: "Invalid File Type",
        body: `Please upload a ${types.join(" or ")} file.`,
        variant: AlertDialogVariants.ERROR,
      });
      return;
    }

    const setPreview = (file: File) => {
      const objectUrl = URL.createObjectURL(file);

      setUploaders(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          localFile: file,
          previewUrl: objectUrl,
        },
      }));
    };

    // FILE DIMENSIONS CHECK
    let expectedDimensions: { width: number; height: number } | undefined;
    if (selectedUploader === "Network Logos") {
      expectedDimensions = { width: 100, height: 100 };
    } else if (selectedUploader === "Client Creative Thumbnail") {
      expectedDimensions = { width: 640, height: 352 };
    } else if (IMAGE_DIMENSIONS[key]) {
      expectedDimensions = IMAGE_DIMENSIONS[key];
    }
    if (expectedDimensions) {
      (async () => {
        try {
          const arrayBuffer = await selectedFile.arrayBuffer();
          const image = await Jimp.read(arrayBuffer);
          image.contain({
            w: expectedDimensions.width,
            h: expectedDimensions.height,
          });

          const base64 = await image.getBase64("image/png");
          const blob = await fetch(base64).then(res => res.blob());
          const croppedFile = new File([blob], key, { type: "image/png" });

          setPreview(croppedFile);
        } catch (err) {
          if (err instanceof Error) {
            setError({ message: err.message, reportError: err });
          }
        }
      })();
    } else {
      setPreview(selectedFile);
    }
  };

  const handleS3Upload = async () => {
    if (!Object.values(uploaders).some(uploader => uploader.localFile)) {
      setDialog({
        show: true,
        title: "No File Selected",
        body: "Please select a file to upload.",
        variant: AlertDialogVariants.ERROR,
      });
      return;
    }
    setIsUploading(true);
    try {
      // wraps filereader in a promise
      const readFileAsDataURL = (file: File) => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(reader.result as string);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      };

      const uploadPromises = Object.keys(uploaders).map(async key => {
        const uploader = uploaders[key];
        if (!uploader.localFile) {
          return;
        }
        try {
          const base64data = await readFileAsDataURL(uploader.localFile);
          try {
            await S3Delete(bucketName, uploader.s3Key!);
          } catch (err) {} // we don't really care if this fails, just need to catch the err
          try {
            await S3PutPng(bucketName, uploader.s3Key!, base64data);
          } catch (err) {
            setDialog({
              show: true,
              title: "Upload Failed",
              body: `Failed to upload ${uploader.title}: ${err}`,
              variant: AlertDialogVariants.ERROR,
            });
          }
        } catch (err) {
          setDialog({
            show: true,
            title: "Upload Failed",
            body: `Failed to upload ${uploader.title}: ${err}`,
            variant: AlertDialogVariants.ERROR,
          });
        }
      });

      // only set success dialog if all uploads succeed
      await Promise.all(uploadPromises);
      setDialog({
        show: true,
        title: "Upload Complete",
        body: "All uploads finished successfully.",
        variant: AlertDialogVariants.SUCCESS,
      });
      setUploaders(getClearUploaderState());
    } catch (err) {
      if (err instanceof Error) {
        setError({ message: err.message, reportError: err });
      }
    }
    setIsUploading(false);
  };

  return (
    <Page app2Redesign title="Image Uploader" pageType={"Image Uploader"}>
      <div className="cdnWidget">
        <div className="cdnHeader">
          <div className="cdnHeaderTitle">Image Uploader</div>
        </div>
        <div className="cdnBody">
          <div className="dropdownContainer">
            <div className="dropdownAndLabel">
              <div className="dropdownLabel">Image Type</div>
              <Dropdown
                className="dropdown"
                value={selectedUploader}
                options={MAIN_DROPDOWN_OPTIONS}
                onChange={selection => {
                  setSelectedUploader(selection);
                  setSelectedCompany("");
                }}
                type={DropdownToggleType.OUTLINED}
              />
            </div>
            {selectedUploader === "Network Logos" && (
              <div className="dropdownAndLabel">
                <div className="dropdownLabel">Network</div>
                <Dropdown
                  className="networkDropdown"
                  type={DropdownToggleType.OUTLINED}
                  value={selectedNetwork}
                  options={NETWORK_DROPDOWN_OPTIONS}
                  onChange={selection => setSelectedNetwork(selection)}
                />
              </div>
            )}
            {selectedUploader !== "Network Logos" && (
              <div className="dropdownAndLabel">
                <div className="dropdownLabel">Client</div>
                <div className="companyContainer">
                  <OverlayTrigger
                    customTrigger={R.defaultTo(false, newCompanySelector)}
                    exitOnBackgroundClick={true}
                    loseFocusOnScroll={false}
                    onExit={() => {
                      setNewCompanySelector(undefined);
                    }}
                    overlay={
                      <CompanySelector
                        navBar={false}
                        company={selectedCompany}
                        setCompany={newCompany => {
                          setSelectedCompany(newCompany);
                          setSelectedISCIs([]);
                        }}
                        newCompanySelector={newCompanySelector}
                        setNewCompanySelector={setNewCompanySelector}
                        companySearchQuery={companySearchQuery}
                        setCompanySearchQuery={setCompanySearchQuery}
                      />
                    }
                    placement={OverlayTrigger.PLACEMENTS.BOTTOM.LEFT}
                  >
                    <div
                      className="companyDropdownContainer"
                      onClick={() => {
                        setNewCompanySelector(true);
                      }}
                    >
                      <div className="companyDropdown">
                        {!selectedCompany ? "Select Client" : selectedCompany}
                      </div>
                      <OverlayTrigger
                        placement={OverlayTrigger.PLACEMENTS.RIGHT.CENTER}
                        overlay={<Tooltip id={uuid.v4()}>Pick a client</Tooltip>}
                      >
                        {<IoMdArrowDropdown className="companySelectArrow" />}
                      </OverlayTrigger>
                    </div>
                  </OverlayTrigger>
                </div>
              </div>
            )}
            {selectedUploader === "Client Creative Thumbnail" && selectedCompany && (
              <div className="dropdownAndLabel">
                <div className="dropdownLabel">ISCI</div>
                <Select
                  className="isciSelect"
                  classNamePrefix="isciSelect"
                  isMulti
                  isClearable
                  isLoading={!(ISCI_DROPDOWN_OPTIONS.length > 0)}
                  options={ISCI_DROPDOWN_OPTIONS}
                  value={selectedISCIs.map(isci => ({ label: isci, value: isci }))}
                  onChange={handleISCIChange}
                  placeholder="Select ISCI(s)"
                />
              </div>
            )}
          </div>
          <div className="fileInputContainer">
            {uploaders &&
              Object.keys(uploaders).map((key, index) => (
                <>
                  <ImageUploadPreview
                    key={key}
                    label={uploaders[key].title}
                    filename={key}
                    localUrl={uploaders[key].previewUrl ?? undefined}
                    s3Url={s3Urls[key] ?? undefined}
                    onChange={handleMediaUpload(key)}
                    onRemove={() => {
                      setUploaders(prev => ({
                        ...prev,
                        [key]: {
                          ...prev[key],
                          localFile: null,
                          previewUrl: null,
                          isLoading: false,
                        },
                      }));
                    }}
                    disabled={uploaders[key].isLoading || isUploading}
                    type={
                      selectedUploader as
                        | "Client Logos"
                        | "Client Creative Thumbnail"
                        | "Client Intro Slide"
                        | "Network Logos"
                    }
                  />
                  {index < Object.keys(uploaders).length - 1 && (
                    <div className="fileInputDivider"></div>
                  )}
                </>
              ))}
          </div>
        </div>
        <InfoSidebar infoList={FILE_RESTRICTIONS_INFO} showHelpButton={true} />
        <div className="cdnFooter">
          <Button
            type={ButtonType.OUTLINED}
            icon={<MdOutlineDeleteOutline />}
            variant={ButtonFrameworkVariant.LEADING_ICON}
            onClick={() => setUploaders(getClearUploaderState)}
            disabled={
              !completeInfo ||
              isUploading ||
              !Object.values(uploaders).some(uploader => uploader.localFile)
            }
          >
            Discard Edits
          </Button>
          <Button
            type={ButtonType.OUTLINED}
            icon={<MdSave />}
            variant={ButtonFrameworkVariant.LEADING_ICON}
            onClick={handleS3Upload}
            disabled={
              !completeInfo ||
              isUploading ||
              !Object.values(uploaders).some(uploader => uploader.localFile)
            }
          >
            Upload
          </Button>
        </div>
      </div>
      {dialog && (
        <AlertDialog
          show={dialog.show}
          onHide={() => setDialog(null)}
          icon={null}
          title={dialog.title}
          body={dialog.body}
          primaryButtonLabel="Close"
          variant={dialog.variant}
        />
      )}
    </Page>
  );
};

export default ImageUploader;
